/**
 * Unified Financial Calculation Engine
 * 
 * This module provides consistent calculations for both the homepage demo
 * and the full financial analysis page. All calculations must match the
 * backend engine to ensure consistency.
 * 
 * IMPORTANT: This mirrors the backend financial_assumptions.py and 
 * retirement_gap_engine.py calculations exactly.
 */

// Financial Assumptions - MUST match backend/financial_assumptions.py
export const ASSUMPTIONS = {
  // Market Returns
  GROSS_MARKET_RETURN: 0.07, // 7% gross market return
  
  // ETF Depot Costs and Taxation
  ETF_ANNUAL_COSTS: 0.003,       // 0.3% TER + platform
  ETF_ANNUAL_TAX_DRAG: 0.008,    // 0.8% annual tax drag
  ETF_CAPITAL_GAINS_TAX_RATE: 0.26375, // Abgeltungssteuer + Soli
  
  // Policy Costs
  POLICY_ANNUAL_COSTS: 0.006,    // 0.6% ongoing costs
  POLICY_UPFRONT_COSTS: 0.01,    // 1.0% initial costs
  
  // Retirement Planning
  TARGET_INCOME_REPLACEMENT_RATE: 0.80,
  STATUTORY_PENSION_REPLACEMENT_RATE: 0.35,
  WITHDRAWAL_RATE: 0.035,        // 3.5% safe withdrawal rate
  RETIREMENT_YEARS: 30,
  
  // Inflation
  INFLATION_RATE: 0.02,
  SALARY_GROWTH_RATE: 0.02,
  
  // Savings Account
  SAVINGS_ACCOUNT_RETURN: 0.005, // 0.5% return
};

/**
 * Project wealth accumulation with monthly compounding
 * MUST match backend _project_wealth function exactly
 */
export const projectWealth = (
  currentAssets,
  monthlyContribution,
  years,
  annualReturn,
  annualCosts = 0
) => {
  const netAnnualReturn = annualReturn - annualCosts;
  const monthlyNetReturn = Math.pow(1 + netAnnualReturn, 1/12) - 1;
  
  let wealth = currentAssets;
  const totalMonths = years * 12;
  const series = [];
  
  for (let month = 0; month <= totalMonths; month++) {
    series.push({
      month,
      year: month / 12,
      wealth: Math.round(wealth),
    });
    
    if (month < totalMonths) {
      wealth = wealth * (1 + monthlyNetReturn) + monthlyContribution;
    }
  }
  
  return {
    finalWealth: Math.round(wealth),
    series,
    totalContributions: currentAssets + (monthlyContribution * totalMonths),
    totalGains: wealth - (currentAssets + (monthlyContribution * totalMonths)),
  };
};

/**
 * Calculate ETF Depot final value after all taxes
 */
export const calculateEtfDepotFinalValue = (
  initialCapital,
  monthlyContribution,
  years
) => {
  const grossReturn = ASSUMPTIONS.GROSS_MARKET_RETURN;
  const costs = ASSUMPTIONS.ETF_ANNUAL_COSTS;
  const taxDrag = ASSUMPTIONS.ETF_ANNUAL_TAX_DRAG;
  const netReturn = grossReturn - costs - taxDrag;
  
  const projection = projectWealth(initialCapital, monthlyContribution, years, netReturn, 0);
  const grossValue = projection.finalWealth;
  const totalContributions = projection.totalContributions;
  const gains = grossValue - totalContributions;
  
  // Apply capital gains tax on profits
  const tax = gains * ASSUMPTIONS.ETF_CAPITAL_GAINS_TAX_RATE;
  const netValue = grossValue - Math.max(0, tax);
  
  return {
    grossValue: Math.round(grossValue),
    netValue: Math.round(netValue),
    totalContributions: Math.round(totalContributions),
    totalGains: Math.round(gains),
    totalTax: Math.round(Math.max(0, tax)),
    effectiveReturn: netReturn,
    series: projection.series,
  };
};

/**
 * Calculate ETF Policy final value after all taxes
 */
export const calculatePolicyFinalValue = (
  initialCapital,
  monthlyContribution,
  years,
  currentAge = 35
) => {
  const grossReturn = ASSUMPTIONS.GROSS_MARKET_RETURN;
  const costs = ASSUMPTIONS.POLICY_ANNUAL_COSTS;
  const netReturn = grossReturn - costs;
  
  // Apply upfront costs to initial capital
  const adjustedInitialCapital = initialCapital * (1 - ASSUMPTIONS.POLICY_UPFRONT_COSTS);
  
  const projection = projectWealth(adjustedInitialCapital, monthlyContribution, years, netReturn, 0);
  const grossValue = projection.finalWealth;
  const totalContributions = initialCapital + (monthlyContribution * years * 12);
  const gains = grossValue - totalContributions;
  
  // Check if policy qualifies for 50% tax exemption (12 years + age 62)
  const endAge = currentAge + years;
  const qualifiesForExemption = years >= 12 && endAge >= 62;
  
  let tax = 0;
  if (qualifiesForExemption) {
    // 50% of gains taxed at personal rate (assume 25%)
    tax = (gains * 0.5) * 0.25;
  } else {
    // Full gains taxed
    tax = gains * ASSUMPTIONS.ETF_CAPITAL_GAINS_TAX_RATE;
  }
  
  const netValue = grossValue - Math.max(0, tax);
  
  return {
    grossValue: Math.round(grossValue),
    netValue: Math.round(netValue),
    totalContributions: Math.round(totalContributions),
    totalGains: Math.round(gains),
    totalTax: Math.round(Math.max(0, tax)),
    effectiveReturn: netReturn,
    qualifiesForExemption,
    series: projection.series,
  };
};

/**
 * Calculate savings account final value
 */
export const calculateSavingsAccountValue = (
  initialCapital,
  monthlyContribution,
  years
) => {
  const projection = projectWealth(
    initialCapital, 
    monthlyContribution, 
    years, 
    ASSUMPTIONS.SAVINGS_ACCOUNT_RETURN, 
    0
  );
  
  return {
    finalValue: Math.round(projection.finalWealth),
    totalContributions: Math.round(projection.totalContributions),
    series: projection.series,
  };
};

/**
 * Calculate coordinated strategy (optimized combination)
 * This represents the best-case scenario with full optimization
 */
export const calculateCoordinatedStrategy = (
  initialCapital,
  monthlyContribution,
  years,
  currentAge = 35
) => {
  // Coordinated strategy uses policy as base but with additional optimization bonus
  const policyResult = calculatePolicyFinalValue(initialCapital, monthlyContribution, years, currentAge);
  
  // Add coordination bonus (better allocation, timing, rebalancing)
  const coordinationBonus = 1.08; // 8% improvement from coordination
  const optimizedValue = Math.round(policyResult.netValue * coordinationBonus);
  
  return {
    grossValue: Math.round(policyResult.grossValue * coordinationBonus),
    netValue: optimizedValue,
    totalContributions: policyResult.totalContributions,
    coordinationBonus: coordinationBonus,
    series: policyResult.series.map(p => ({
      ...p,
      wealth: Math.round(p.wealth * coordinationBonus),
    })),
  };
};

/**
 * Calculate monthly pension from capital using withdrawal rate
 */
export const calculateMonthlyPension = (capital) => {
  return Math.round((capital * ASSUMPTIONS.WITHDRAWAL_RATE) / 12);
};

/**
 * Calculate statutory pension estimate
 */
export const calculateStatutoryPension = (currentNetIncome) => {
  return Math.round(currentNetIncome * ASSUMPTIONS.STATUTORY_PENSION_REPLACEMENT_RATE);
};

/**
 * Calculate total retirement income
 */
export const calculateTotalRetirementIncome = (
  projectedCapital,
  currentNetIncome,
  privateRetirementIncome = 0
) => {
  const capitalPension = calculateMonthlyPension(projectedCapital);
  const statutoryPension = calculateStatutoryPension(currentNetIncome);
  
  return {
    capitalPension,
    statutoryPension,
    privateRetirementIncome,
    totalPension: capitalPension + statutoryPension + privateRetirementIncome,
  };
};

/**
 * Calculate required capital for target pension
 */
export const calculateRequiredCapital = (
  targetPension,
  statutoryPension,
  privateRetirementIncome = 0
) => {
  const requiredFromCapital = Math.max(0, targetPension - statutoryPension - privateRetirementIncome);
  const requiredCapital = (requiredFromCapital * 12) / ASSUMPTIONS.WITHDRAWAL_RATE;
  return Math.round(requiredCapital);
};

/**
 * Full quick analysis for homepage demo
 * Returns all key metrics using the unified calculation engine
 */
export const calculateQuickAnalysis = (
  monthlyContribution,
  yearsToRetirement,
  initialCapital = 0,
  currentAge = 35,
  currentNetIncome = 3000
) => {
  // Calculate all three scenarios
  const savings = calculateSavingsAccountValue(initialCapital, monthlyContribution, yearsToRetirement);
  const etfDepot = calculateEtfDepotFinalValue(initialCapital, monthlyContribution, yearsToRetirement);
  const coordinated = calculateCoordinatedStrategy(initialCapital, monthlyContribution, yearsToRetirement, currentAge);
  
  // Calculate pension potential
  const statutoryPension = calculateStatutoryPension(currentNetIncome);
  const etfPension = calculateMonthlyPension(etfDepot.netValue);
  const coordinatedPension = calculateMonthlyPension(coordinated.netValue);
  
  // Total pensions including statutory
  const etfTotalPension = etfPension + statutoryPension;
  const coordinatedTotalPension = coordinatedPension + statutoryPension;
  
  // Target pension (80% of income)
  const targetPension = Math.round(currentNetIncome * ASSUMPTIONS.TARGET_INCOME_REPLACEMENT_RATE);
  
  return {
    // Capital projections
    savings: savings.finalValue,
    etfDepot: etfDepot.netValue,
    coordinated: coordinated.netValue,
    totalContributions: initialCapital + (monthlyContribution * yearsToRetirement * 12),
    
    // Pension projections (including statutory)
    etfPension: etfTotalPension,
    coordinatedPension: coordinatedTotalPension,
    statutoryPension,
    
    // Target comparison
    targetPension,
    
    // Advantage calculations
    advantageVsSavings: coordinated.netValue - savings.finalValue,
    advantageVsEtf: coordinated.netValue - etfDepot.netValue,
    pensionAdvantage: coordinatedTotalPension - etfTotalPension,
    
    // Goal achievement
    goalAchieved: coordinatedTotalPension >= targetPension,
    achievementPercentage: Math.round((coordinatedTotalPension / targetPension) * 100),
    
    // Assumptions used
    assumptions: { ...ASSUMPTIONS },
  };
};

export default {
  ASSUMPTIONS,
  projectWealth,
  calculateEtfDepotFinalValue,
  calculatePolicyFinalValue,
  calculateSavingsAccountValue,
  calculateCoordinatedStrategy,
  calculateMonthlyPension,
  calculateStatutoryPension,
  calculateTotalRetirementIncome,
  calculateRequiredCapital,
  calculateQuickAnalysis,
};
